// Apolo IDE — PlanSidebar (sprint plan-tab-v1, E2).
//
// Lista de planos do usuário + busca + "+ Novo plano". Cada item mostra
// emoji + nome + status pill. Selecionado tem glow violeta.

import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Plus, Search, Sparkles, MoreHorizontal, Archive } from 'lucide-react';

export default function PlanSidebar({
  plans,
  activePlanId,
  loading,
  error,
  onSelect,
  onCreate,
  onRefresh,
  onDelete,
  onPatch,
  mobile = false,
}) {
  const [query, setQuery] = useState('');
  const [menuOpenFor, setMenuOpenFor] = useState(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return plans;
    const q = query.trim().toLowerCase();
    return plans.filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [plans, query]);

  return (
    <aside
      aria-label="Planos"
      className={`flex h-full ${mobile ? 'w-full' : 'w-[280px]'} flex-col border-r border-apolo-borderSubtle bg-apolo-surface1`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-apolo-borderSubtle px-3 py-3">
        <span
          aria-hidden="true"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md"
          style={{ background: 'var(--apolo-plan-gradient)', boxShadow: 'var(--apolo-plan-glow-tight)' }}
        >
          <Sparkles size={14} className="text-white" />
        </span>
        <span className="text-sm font-semibold tracking-tight">Plan</span>
        <span className="ml-auto text-[11px] text-apolo-textTertiary">{plans.length}</span>
      </div>

      {/* Search + create */}
      <div className="flex items-center gap-2 px-3 py-2">
        <label className="relative flex flex-1 items-center">
          <Search size={14} className="absolute left-2 text-apolo-textTertiary" aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar plano..."
            className="h-9 w-full rounded-md border border-apolo-borderSubtle bg-apolo-bg pl-7 pr-2 text-sm text-apolo-textPrimary placeholder:text-apolo-textPlaceholder focus:border-apolo-plan-accentBorder focus:outline-none focus:ring-2 focus:ring-apolo-plan-accentRing"
          />
        </label>
        <button
          type="button"
          onClick={onCreate}
          aria-label="Novo plano"
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-white transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-plan-accentRing"
          style={{ background: 'var(--apolo-plan-gradient)', boxShadow: 'var(--apolo-plan-glow-tight)' }}
        >
          <Plus size={16} />
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-2 py-1" role="listbox" aria-label="Lista de planos">
        {loading && plans.length === 0 ? (
          <div className="flex items-center gap-2 px-2 py-3 text-xs text-apolo-textTertiary">
            <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            Carregando planos…
          </div>
        ) : error ? (
          <div className="flex flex-col gap-2 px-2 py-3 text-xs text-apolo-error">
            <span>{error}</span>
            <button type="button" onClick={onRefresh} className="self-start rounded-md border border-apolo-error/40 bg-apolo-error/10 px-2 py-1 text-[11px] hover:bg-apolo-error/20">
              Tentar de novo
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-2 py-6 text-center">
            <p className="text-xs text-apolo-textTertiary">
              {query ? 'Nenhum plano com esse nome.' : 'Nenhum plano ainda.'}
            </p>
            <button
              type="button"
              onClick={onCreate}
              className="mt-3 inline-flex items-center gap-1 rounded-md border border-apolo-plan-accentBorder bg-apolo-plan-accentMuted px-2.5 py-1.5 text-xs font-medium text-apolo-plan-accent hover:brightness-110"
            >
              <Plus size={12} /> Criar primeiro plano
            </button>
          </div>
        ) : (
          <ul className="flex flex-col gap-1 py-1">
            {filtered.map((plan) => {
              const isActive = plan.id === activePlanId;
              const menuOpen = menuOpenFor === plan.id;
              return (
                <li key={plan.id} className="relative">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    onClick={() => onSelect(plan.id)}
                    className={`group flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-plan-accentRing ${
                      isActive
                        ? 'border border-apolo-plan-accentBorder bg-apolo-plan-accentMuted text-apolo-textPrimary'
                        : 'border border-transparent text-apolo-textSecondary hover:bg-apolo-surface2 hover:text-apolo-textPrimary'
                    }`}
                    style={isActive ? { boxShadow: 'var(--apolo-plan-glow-tight)' } : undefined}
                  >
                    {isActive ? (
                      <motion.span
                        layoutId="plan-active-marker"
                        aria-hidden="true"
                        className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full"
                        style={{ background: 'var(--apolo-plan-gradient)' }}
                      />
                    ) : null}
                    <span aria-hidden="true" className="text-base leading-none">{plan.emoji || '📋'}</span>
                    <span className="flex-1 truncate font-medium">{plan.name}</span>
                    <span className={`text-[10px] uppercase tracking-wide ${statusToneClass(plan.status)}`}>
                      {plan.status}
                    </span>
                    <span
                      onClick={(e) => { e.stopPropagation(); setMenuOpenFor(menuOpen ? null : plan.id); }}
                      className="invisible inline-flex h-6 w-6 items-center justify-center rounded text-apolo-textTertiary hover:bg-apolo-surface3 hover:text-apolo-textPrimary group-hover:visible"
                      role="button"
                      aria-haspopup="menu"
                      aria-expanded={menuOpen}
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setMenuOpenFor(menuOpen ? null : plan.id); } }}
                    >
                      <MoreHorizontal size={12} />
                    </span>
                  </button>
                  {menuOpen ? (
                    <div role="menu" className="absolute right-2 top-9 z-20 w-44 overflow-hidden rounded-md border border-apolo-borderSubtle bg-apolo-surface2 shadow-lg">
                      <button
                        type="button"
                        onClick={async () => {
                          const next = window.prompt('Renomear plano', plan.name);
                          if (next && next.trim() && next !== plan.name) await onPatch(plan.id, { name: next.trim() });
                          setMenuOpenFor(null);
                        }}
                        className="block w-full px-3 py-2 text-left text-xs text-apolo-textPrimary hover:bg-apolo-surface3"
                        role="menuitem"
                      >
                        Renomear
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (window.confirm(`Arquivar "${plan.name}"?`)) await onDelete(plan.id);
                          setMenuOpenFor(null);
                        }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-apolo-error hover:bg-apolo-errorBg/30"
                        role="menuitem"
                      >
                        <Archive size={12} /> Arquivar
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="border-t border-apolo-borderSubtle px-3 py-2 text-[10px] text-apolo-textTertiary">
        plan-tab-v1 · canvas mode
      </div>
    </aside>
  );
}

function statusToneClass(status) {
  if (status === 'active')   return 'text-apolo-success';
  if (status === 'exported') return 'text-apolo-plan-accent';
  if (status === 'archived') return 'text-apolo-textTertiary';
  return 'text-apolo-textTertiary'; // draft default
}
